import { onActivated, ref, watch } from 'vue';
import { buildCountryOptions, normalizeCountry } from '@/utils/countryCatalog';

const STORAGE_KEY = 'hr.filter.countryScope';
const LEGACY_KEY = 'hr.filter.moldovaOnly';

export const COUNTRY_SCOPE_ALL = 'all';
export const COUNTRY_SCOPE_US = 'US';
export const COUNTRY_SCOPE_MOLDOVA = 'Moldova';

/** All, plus every catalog country (US, Moldova, UK, Spain, and location countries when passed). */
export function buildCountryScopeOptions(locations = [], extra = [], options = {}) {
  return [
    { label: 'All', value: COUNTRY_SCOPE_ALL },
    ...buildCountryOptions(locations, extra, options),
  ];
}

export const COUNTRY_SCOPE_OPTIONS = Object.freeze(buildCountryScopeOptions());

/**
 * @param {unknown} value
 * @returns {string} `all` or a canonical country
 */
export function normalizeCountryScope(value) {
  if (typeof value !== 'string') return COUNTRY_SCOPE_ALL;
  if (value == null || value === '' || value === COUNTRY_SCOPE_ALL) return COUNTRY_SCOPE_ALL;
  if (String(value).trim().toLowerCase() === 'all') return COUNTRY_SCOPE_ALL;
  return normalizeCountry(value) || COUNTRY_SCOPE_ALL;
}

function readStored() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored != null) return normalizeCountryScope(stored);
    if (localStorage.getItem(LEGACY_KEY) === '1') return COUNTRY_SCOPE_MOLDOVA;
    return COUNTRY_SCOPE_ALL;
  } catch {
    return COUNTRY_SCOPE_ALL;
  }
}

/** Shared across list pages. Persists until set back to All. */
export const countryScope = ref(readStored());
let accountId = null;

/** Selection belongs to the signed-in account on this browser. Migrate the old filter once. */
export function initializeWorkspaceCountry(userId) {
  const nextId = userId ? String(userId) : null;
  if (nextId === accountId) return;
  accountId = nextId;
  let next = COUNTRY_SCOPE_ALL;
  try {
    if (accountId) {
      const stored = localStorage.getItem(`${STORAGE_KEY}.${accountId}`);
      next = stored == null ? readStored() : normalizeCountryScope(stored);
      localStorage.setItem(`${STORAGE_KEY}.${accountId}`, next);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_KEY);
    }
  } catch { /* Keep a usable in-memory workspace in private mode. */ }
  countryScope.value = next;
}

export function setCountryScope(value) {
  const next = normalizeCountryScope(value);
  if (countryScope.value === next) return;
  countryScope.value = next;
  try {
    localStorage.removeItem(LEGACY_KEY);
    const key = accountId ? `${STORAGE_KEY}.${accountId}` : STORAGE_KEY;
    localStorage.setItem(key, next);
  } catch {
    /* ignore quota / private mode */
  }
}

/** `country` body for list APIs, or null for All. Aliases such as USA are stored as US. */
export function countryScopeApiValue() {
  if (!countryScope.value || countryScope.value === COUNTRY_SCOPE_ALL) return null;
  return countryScope.value;
}

export function isCountryScopeActive() {
  return countryScopeApiValue() != null;
}

export function useCountryScopeFilter() {
  return { countryScope, setCountryScope, countryScopeApiValue, isCountryScopeActive };
}

/**
 * Country is workspace context, independent of a page's clear-filter action.
 * Retained for existing list callers; selection changes only in the sidebar.
 */
export function clearCountryScopeForReload() {}

/**
 * Reload the active list when the scope changes, and again when a
 * keep-alive page is shown under a different value than its last load.
 */
export function useCountryScopeListReload(loadedOnce, reload) {
  let loadedFor = countryScope.value;
  let skip = false;

  function markLoaded() {
    loadedFor = countryScope.value;
  }

  function skipNextChange() {
    skip = true;
  }

  watch(
    countryScope,
    () => {
      if (skip) {
        skip = false;
        return;
      }
      reload();
    },
    { flush: 'post' }
  );

  onActivated(() => {
    if (!loadedOnce?.value) return;
    if (loadedFor !== countryScope.value) reload();
  });

  return { markLoaded, skipNextChange };
}
